/**
 * Parsing helpers for GitLab merge-request URLs pasted into the app. The
 * GitLab twin of `github-url.ts`: same mid-text matching, same canonical-form
 * output, so provider-neutral surfaces (the shared PR chip, content scanners)
 * treat an MR link an agent printed the way they treat a PR link.
 */

/**
 * Source of the merge-request URL pattern, as a string so surfaces that can
 * only carry a serialized pattern declare the same shape this module parses
 * instead of hand-copying it. Two capture groups: project path, MR iid. The
 * project path is `namespace/project` with any depth of nested groups
 * (`group/subgroup/project`), which is why it is one capture rather than the
 * owner/repo split GitHub gets.
 *
 * The pattern matches mid-text (a link written into a sentence or printed by
 * `glab mr create` is the normal case), so `gitlab.com` is guarded by a
 * lookbehind rather than an anchor: it has to start a host, not merely appear
 * somewhere. Without it, `notgitlab.com/a/b/-/merge_requests/1` and — worse,
 * since a pasted link becomes a reference chip —
 * `evil.example/gitlab.com/a/b/-/merge_requests/1` or
 * `evil.example/?u=gitlab.com/a/b/-/merge_requests/1` would all read as that
 * merge request.
 *
 * The iid is closed by a negative lookahead so only a real MR-URL boundary
 * ends it: a subpath (`/diffs`, `/commits`, `/pipelines`), a raw-view suffix
 * (`.diff`, `.patch`), a `?query`, a `#fragment` (`#note_123` on a link copied
 * out of a discussion), or the end of the link. Without it,
 * `…/merge_requests/12ab` would parse as MR 12.
 */
export const GITLAB_MR_URL_SOURCE =
  '(?<![\\w.@/:=?&#-])(?:https?://)?(?:www\\.)?gitlab\\.com/((?:[A-Za-z0-9_.-]+/)+?[A-Za-z0-9_.-]+)/-/merge_requests/(\\d+)(?![\\w-])';

const GITLAB_MR_URL = new RegExp(GITLAB_MR_URL_SOURCE, 'i');
const GITLAB_MR_URL_GLOBAL = new RegExp(GITLAB_MR_URL_SOURCE, 'gi');

export interface GitLabMrRef {
  /** Full project path, nested groups included: `group/subgroup/project`. */
  projectPath: string;
  /** The MR's iid — the per-project number in the URL, not the global id. */
  iid: number;
}

/**
 * Extract the first GitLab merge-request reference from a string, or `null`
 * when there is none. Accepts bare and `https://`-prefixed forms, and every
 * artifact a link picks up on its way through a browser or a discussion: a
 * subpath (`/diffs`, `/commits`, `/pipelines`), a `.diff` / `.patch` raw view,
 * a query string, and a `#note_…` fragment all resolve to the same MR.
 */
export function parseGitLabMrUrl(text: string): GitLabMrRef | null {
  const match = text.match(GITLAB_MR_URL);
  if (!match) return null;
  return { projectPath: match[1], iid: Number(match[2]) };
}

/** A merge request named somewhere in a block of text, in canonical form. */
export interface GitLabMrLink extends GitLabMrRef {
  /** `group/project!42` — GitLab's canonical cross-project reference form. */
  externalId: string;
  /** Canonical `https://gitlab.com/group/project/-/merge_requests/42`, however it was written. */
  url: string;
}

/**
 * Every distinct merge request named in `text`, in the order they appear.
 *
 * Used where an MR link is buried in output nobody parsed — `glab mr create`
 * printing the URL it just opened, an agent quoting a link — so the surface
 * can present the MR itself rather than a bare URL. Deduped by canonical id,
 * so the same MR written twice (or in two spellings) yields one entry.
 */
export function parseGitLabMrUrls(text: string): GitLabMrLink[] {
  const links: GitLabMrLink[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(GITLAB_MR_URL_GLOBAL)) {
    const projectPath = match[1];
    const iid = Number(match[2]);
    const externalId = `${projectPath}!${iid}`;
    if (seen.has(externalId)) continue;
    seen.add(externalId);
    links.push({
      projectPath,
      iid,
      externalId,
      url: `https://gitlab.com/${projectPath}/-/merge_requests/${iid}`,
    });
  }
  return links;
}
